import type { SceneId, TargetKind } from '../sim/types';

const mtof = (m: number) => 440 * Math.pow(2, (m - 69) / 12);

/** Every sound is synthesized with Web Audio: no audio files. */
export class AudioEngine {
  ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private ambience: { src: AudioBufferSourceNode; gain: GainNode; scene: SceneId } | null = null;
  sfxOn = true;
  musicOn = true;
  volume = 0.8;

  /** Browsers only allow audio after a tap or key press; call this from those events. */
  unlock(): void {
    try {
      if (!this.ctx) {
        const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!AC) return;
        const ctx = new AC({ latencyHint: 'interactive' });
        this.ctx = ctx;
        this.master = ctx.createGain();
        this.master.connect(ctx.destination);
        this.sfxBus = ctx.createGain();
        this.sfxBus.connect(this.master);
        this.musicBus = ctx.createGain();
        this.musicBus.connect(this.master);
        const len = ctx.sampleRate * 2;
        this.noise = ctx.createBuffer(1, len, ctx.sampleRate);
        const data = this.noise.getChannelData(0);
        for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
        this.applyLevels();
      }
      if (this.ctx.state === 'suspended') void this.ctx.resume();
    } catch {
      // no audio on this device
    }
  }

  get ready(): boolean {
    return !!this.ctx && this.ctx.state === 'running';
  }

  get now(): number {
    return this.ctx ? this.ctx.currentTime : 0;
  }

  /** Seconds between scheduling a sound and hearing it. */
  get outputLatency(): number {
    const c = this.ctx as (AudioContext & { outputLatency?: number }) | null;
    return c ? (c.outputLatency ?? 0) + (c.baseLatency ?? 0) : 0;
  }

  set(opts: { sfx: boolean; music: boolean; volume: number }): void {
    this.sfxOn = opts.sfx;
    this.musicOn = opts.music;
    this.volume = opts.volume;
    this.applyLevels();
  }

  private applyLevels(): void {
    if (!this.ctx || !this.master || !this.sfxBus || !this.musicBus) return;
    const t = this.ctx.currentTime;
    this.master.gain.setTargetAtTime(this.volume, t, 0.02);
    this.sfxBus.gain.setTargetAtTime(this.sfxOn ? 1 : 0, t, 0.02);
    this.musicBus.gain.setTargetAtTime(this.musicOn ? 0.55 : 0, t, 0.02);
    if (this.ambience) this.ambience.gain.gain.setTargetAtTime(this.musicOn ? this.ambienceLevel(this.ambience.scene) : 0, t, 0.2);
  }

  // ---------- building blocks ----------

  private env(t0: number, peak: number, attack: number, hold: number, release: number, bus: GainNode): GainNode {
    const g = this.ctx!.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(peak, t0 + attack);
    g.gain.setValueAtTime(peak, t0 + attack + hold);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + attack + hold + release);
    g.connect(bus);
    return g;
  }

  /** One "rib" or "bit": a buzzy sawtooth pulsed fast, like a frog's throat. */
  private syllable(t0: number, dur: number, f0: number, f1: number, level = 0.9): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(f0, t0);
    osc.frequency.exponentialRampToValueAtTime(f1, t0 + dur);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(1500, t0);
    bp.frequency.exponentialRampToValueAtTime(650, t0 + dur);
    bp.Q.value = 2.2;
    const am = ctx.createGain();
    am.gain.value = 0.5;
    const lfo = ctx.createOscillator();
    lfo.type = 'square';
    lfo.frequency.value = 40;
    const lfoG = ctx.createGain();
    lfoG.gain.value = 0.5;
    lfo.connect(lfoG);
    lfoG.connect(am.gain);
    const env = this.env(t0, level, 0.012, dur * 0.55, dur * 0.45, this.sfxBus!);
    osc.connect(bp);
    bp.connect(am);
    am.connect(env);
    osc.start(t0);
    lfo.start(t0);
    osc.stop(t0 + dur + 0.05);
    lfo.stop(t0 + dur + 0.05);
  }

  private tone(t0: number, freq: number, dur: number, type: OscillatorType, level: number, bus: GainNode, freqEnd?: number): void {
    const ctx = this.ctx!;
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(freq, t0);
    if (freqEnd) o.frequency.exponentialRampToValueAtTime(freqEnd, t0 + dur);
    const g = this.env(t0, level, 0.005, 0, dur, bus);
    o.connect(g);
    o.start(t0);
    o.stop(t0 + dur + 0.05);
  }

  private noiseHit(t0: number, dur: number, freq: number, q: number, level: number, bus: GainNode, type: BiquadFilterType = 'bandpass'): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    const f = ctx.createBiquadFilter();
    f.type = type;
    f.frequency.value = freq;
    f.Q.value = q;
    const g = this.env(t0, level, 0.004, 0, dur, bus);
    src.connect(f);
    f.connect(g);
    src.start(t0, Math.random() * 1.5);
    src.stop(t0 + dur + 0.05);
  }

  // ---------- sound effects ----------

  croak(pitch = 1): void {
    if (!this.ready) return;
    const t = this.now + 0.005;
    const p = pitch * (0.9 + Math.random() * 0.2);
    this.syllable(t, 0.085, 270 * p, 190 * p);
    this.syllable(t + 0.12, 0.14, 310 * p, 165 * p);
  }

  /** A short, high chirp on a perfect jump. */
  perfect(combo: number): void {
    if (!this.ready) return;
    const t = this.now + 0.005;
    const p = 1.5 + Math.min(combo, 12) * 0.04;
    this.syllable(t, 0.06, 420 * p, 360 * p, 0.5);
    this.tone(t, 1320 + Math.min(combo, 12) * 40, 0.12, 'sine', 0.12, this.sfxBus!);
  }

  tangle(): void {
    if (!this.ready) return;
    const ctx = this.ctx!;
    const t = this.now + 0.005;
    const o = ctx.createOscillator();
    o.type = 'triangle';
    o.frequency.setValueAtTime(540, t);
    o.frequency.exponentialRampToValueAtTime(110, t + 0.7);
    const vib = ctx.createOscillator();
    vib.frequency.value = 14;
    const vg = ctx.createGain();
    vg.gain.value = 35;
    vib.connect(vg);
    vg.connect(o.frequency);
    const g = this.env(t, 0.45, 0.02, 0, 0.73, this.sfxBus!);
    o.connect(g);
    o.start(t);
    vib.start(t);
    o.stop(t + 0.8);
    vib.stop(t + 0.8);
    this.syllable(t + 0.8, 0.24, 150, 85);
  }

  tongue(): void {
    if (!this.ready) return;
    this.tone(this.now + 0.005, 900, 0.09, 'sine', 0.15, this.sfxBus!, 300);
  }

  catch(kind: TargetKind): void {
    if (!this.ready) return;
    const t = this.now + 0.005;
    const bus = this.sfxBus!;
    if (kind === 'fly') {
      this.tone(t, 660, 0.08, 'square', 0.08, bus);
      this.tone(t + 0.06, 990, 0.1, 'square', 0.08, bus);
    } else if (kind === 'golden') {
      [784, 988, 1175, 1568].forEach((f, i) => this.tone(t + i * 0.06, f, 0.14, 'triangle', 0.16, bus));
    } else {
      [523, 659, 784, 1047].forEach((f, i) => this.tone(t + i * 0.05, f, 0.16, 'sine', 0.16, bus));
    }
  }

  bubblePop(): void {
    if (!this.ready) return;
    const t = this.now + 0.005;
    this.tone(t, 300, 0.12, 'sine', 0.35, this.sfxBus!, 1400);
    this.noiseHit(t, 0.08, 3000, 1, 0.2, this.sfxBus!);
  }

  thunder(): void {
    if (!this.ready) return;
    const t = this.now + 0.005;
    this.noiseHit(t, 1.2, 180, 0.7, 0.5, this.sfxBus!, 'lowpass');
    this.noiseHit(t + 0.05, 0.25, 1200, 0.8, 0.15, this.sfxBus!);
  }

  click(): void {
    if (!this.ready) return;
    this.tone(this.now + 0.005, 880, 0.05, 'triangle', 0.08, this.sfxBus!);
  }

  // ---------- music primitives (scheduled ahead of time) ----------

  plop(at: number, accent = 1): void {
    if (!this.ready) return;
    this.tone(at, 190 * accent, 0.16, 'sine', 0.5, this.musicBus!, 70);
  }

  /** Same as plop, but audible even with music off (used by calibration). */
  plopSfx(at: number): void {
    if (!this.ready) return;
    this.tone(at, 190, 0.16, 'sine', 0.6, this.sfxBus!, 70);
  }

  bass(at: number, midi: number, dur: number): void {
    if (!this.ready) return;
    this.tone(at, mtof(midi), dur, 'triangle', 0.22, this.musicBus!);
  }

  marimba(at: number, midi: number, level = 0.2): void {
    if (!this.ready) return;
    const f = mtof(midi);
    this.tone(at, f, 0.35, 'sine', level, this.musicBus!);
    this.tone(at, f * 4, 0.06, 'sine', level * 0.25, this.musicBus!);
  }

  tick(at: number): void {
    if (!this.ready) return;
    this.noiseHit(at, 0.03, 8000, 1.5, 0.06, this.musicBus!, 'highpass');
  }

  cricket(at: number): void {
    if (!this.ready) return;
    for (let i = 0; i < 3; i++) this.tone(at + i * 0.045, 4400, 0.03, 'sine', 0.03, this.musicBus!);
  }

  // ---------- ambience loops ----------

  private ambienceLevel(scene: SceneId): number {
    return scene === 'storm' ? 0.16 : scene === 'ice' ? 0.07 : 0;
  }

  setAmbience(scene: SceneId | null): void {
    if (!this.ctx || !this.noise || !this.musicBus) return;
    if (this.ambience && this.ambience.scene === scene) return;
    const t = this.ctx.currentTime;
    if (this.ambience) {
      const old = this.ambience;
      old.gain.gain.setTargetAtTime(0, t, 0.3);
      old.src.stop(t + 1.5);
      this.ambience = null;
    }
    if (!scene || this.ambienceLevel(scene) === 0) return;
    const src = this.ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    const f = this.ctx.createBiquadFilter();
    f.type = scene === 'storm' ? 'lowpass' : 'bandpass';
    f.frequency.value = scene === 'storm' ? 1400 : 500;
    f.Q.value = scene === 'storm' ? 0.5 : 0.8;
    const g = this.ctx.createGain();
    g.gain.value = 0;
    g.gain.setTargetAtTime(this.musicOn ? this.ambienceLevel(scene) : 0, t, 0.6);
    src.connect(f);
    f.connect(g);
    g.connect(this.master!);
    src.start(t);
    this.ambience = { src, gain: g, scene };
  }
}
